import {
  ArrowLeftOutlined,
  CheckOutlined,
  MinusOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { InfoIcon } from "lucide-react";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";

import {
  AI_QUOTA_PAYWALL_CONTACT_SALES_URL,
  AI_QUOTA_PAYWALL_CREDITS_PER_SEAT,
  AI_QUOTA_PAYWALL_ENTERPRISE_FEATURES,
  AI_QUOTA_PAYWALL_ENTERPRISE_PLATFORM_FEATURES,
  AI_QUOTA_PAYWALL_ENTERPRISE_SUPPORT_FEATURES,
  AI_QUOTA_PAYWALL_MAX_SEATS,
  AI_QUOTA_PAYWALL_MIN_SEATS,
  AI_QUOTA_PAYWALL_REASON_CONFIG,
  AI_QUOTA_PAYWALL_STRIPE_CHECKOUT_URL,
  AI_QUOTA_PAYWALL_TEAMS_FEATURES,
  AI_QUOTA_PAYWALL_TEAMS_PLATFORM_FEATURES,
  BillingPlan,
  isCreditsPricing,
  isEnterpriseLikePlan,
  isTrialLikePlan,
} from "@superblocksteam/shared";
import type {
  AiQuotaPaywallReason as AiQuotaReason,
  OrganizationUserDto,
} from "@superblocksteam/shared";

import { Button } from "components/ui/Button";
import { MANAGE_BILLING } from "constants/rbac";
import {
  GAU_PACKS_DOCS_URL,
  GauInText,
  GauPacksInText,
  GauTerm,
} from "core/billing";
import {
  committedPackCredits,
  creditPackOptionsForInterval,
  defaultCreditPack,
} from "core/billing/creditPacks";
import { CreditPackSelect } from "core/billing/CreditPackSelect";
import { buildEndTrialRequestBody } from "core/billing/endTrialRequest";
import { seatlessAiFeatureList } from "core/billing/seatlessFeatures";
import { Modal } from "core/components/Modal";
import { WithTooltip } from "core/components/WithTooltip";
import { Switch } from "core/shadcn/components/ui/switch";
import { isCloudPrem } from "core/utils/deployment";
import { useAuthorizationCheck } from "hooks/ui/rbac/useAuthorizationCheck";
import {
  selectOnlyOrganization,
  selectOnlyOrganizationId,
} from "store/slices/organizations";
import { callServer, HttpMethod } from "store/utils/client";
import logger from "utils/logger";
import { sendErrorUINotification } from "utils/notification";

import { ManagePlanModal } from "../../dashboard/pages/Usage/ManagePlanModal";
import { useDefaultPricing } from "./useDefaultPricing";

type AiQuotaPaywallModalBaseProps = {
  open: boolean;
  reason: AiQuotaReason | undefined;
  onClose: () => void;
  analyticsSurface: string;
  analyticsMetadata?: Record<string, unknown>;
  /** Called when user successfully completes an action (e.g. assigns seat). Use to navigate back to app instead of onClose. */
  onSuccess?: () => void;
  /** Override the Stripe checkout return URL (defaults to "/usage") */
  returnTo?: string;
  /** Override the modal header title */
  titleOverride?: string;
  /**
   * When true, skip the auto-close effect that fires when the billing plan
   * is not TRIAL or TEAMS.  Use this when the caller already validated the
   * quota on the server and wants to show the paywall regardless of the
   * client-side billing plan (which may lag behind due to caching).
   */
  skipPlanCheck?: boolean;
};

const ModalTitleText = styled.span`
  font-weight: 600;
  color: ${(props) => props.theme.colors.GREY_900};
`;

const ModalTitleDivider = styled.span`
  width: 1px;
  height: 16px;
  background: ${(props) => props.theme.colors.GREY_200};
  margin: 0 16px;
  flex-shrink: 0;
`;

const ModalTitleSubtext = styled.span`
  font-size: 12px;
  line-height: 16px;
  font-weight: 400;
  color: ${(props) => props.theme.colors.GREY_500};
`;

const SeatConfigTitleRow = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`;

const SeatConfigTitleCenter = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  margin: 0;
  border: none;
  background: transparent;
  color: #697b8c;
  cursor: pointer;
  font-size: 16px;
  width: 24px;
  height: 24px;

  &:hover {
    color: ${(props) => props.theme.colors.GREY_900};
  }
`;

const PlanCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  border-radius: 9px;
  border: 1px solid ${(props) => props.theme.colors.GREY_100};
  background: ${(props) => props.theme.colors.WHITE};
  padding: 12px;
  height: 100%;

  &[data-highlighted="true"] {
    border: 1px solid ${(props) => props.theme.colors.GREY_100};
  }
`;

const PlanTopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const PlanName = styled.span`
  font-size: 17px;
  font-weight: 500;
  line-height: 150%;
  color: ${(props) => props.theme.colors.GREY_900};
`;

const PriceRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 6px;
  margin-bottom: 8px;
`;

const PlanPrice = styled.span`
  font-size: 33px;
  font-weight: 500;
  line-height: 1.1;
  color: ${(props) => props.theme.colors.GREY_900};
`;

const PlanPriceUnit = styled.span`
  font-size: 12px;
  line-height: 16px;
  color: ${(props) => props.theme.colors.GREY_500};
`;

const AnnualToggleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  line-height: 16px;
  color: ${(props) => props.theme.colors.GREY_900};
`;

const Save20Text = styled.span`
  color: #079d89;
`;

const PlanDescription = styled.div`
  font-size: 12px;
  line-height: 16px;
  color: ${(props) => props.theme.colors.GREY_600};
`;

const PlanCardTop = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const PlanCardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 0;
`;

const CTAButton = styled(Button).attrs({ className: "ai-paywall-text-12" })`
  width: 100%;
  height: 32px;
  border-radius: 7px;
  font-size: 12px;
  line-height: 16px;
  font-weight: 500;
  margin-bottom: 0;
  margin-top: 0;
  border: 1px solid ${(props) => props.theme.colors.GREY_200};
  color: ${(props) => props.theme.colors.GREY_700};
  background: ${(props) => props.theme.colors.WHITE};

  &[data-primary="true"] {
    border: 1px solid ${(props) => props.theme.colors.GREY_900};
    color: ${(props) => props.theme.colors.WHITE};
    background: ${(props) => props.theme.colors.GREY_900};
  }
`;

const FeaturesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FeatureSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const FeatureList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SectionTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: ${(props) => props.theme.colors.GREY_900};
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 12px;
  line-height: 16px;
  color: ${(props) => props.theme.colors.GREY_600};
`;

const IncludesDivider = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  line-height: 16px;
  color: #a4adb7;
  font-weight: 400;

  &::after {
    content: "";
    flex: 1;
    height: 1px;
    background: ${(props) => props.theme.colors.GREY_100};
  }
`;

const SecondScreenBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SeatConfigBodyTop = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const SeatConfigCard = styled.div`
  border: 1px solid ${(props) => props.theme.colors.GREY_100};
  border-radius: 16px;
  overflow: hidden;
  margin-bottom: 0;
`;

const SeatConfigTop = styled.div`
  padding: 12px 16px;
  border-bottom: 1px solid ${(props) => props.theme.colors.GREY_100};
  font-size: 12px;
  line-height: 16px;
  font-weight: 500;
  color: ${(props) => props.theme.colors.GREY_900};
  text-align: center;
`;

const SeatConfigMiddle = styled.div`
  height: 56px;
  padding: 0 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const StepperButton = styled.button`
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border-radius: 50%;
  border: 1px solid ${(props) => props.theme.colors.GREY_200};
  background: ${(props) => props.theme.colors.WHITE};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 400;
  color: ${(props) => props.theme.colors.GREY_700};

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const SeatCount = styled.div`
  font-size: 28px;
  font-weight: 600;
  color: ${(props) => props.theme.colors.GREY_900};
  min-width: 48px;
  text-align: center;
`;

const SeatConfigBottom = styled.div`
  padding: 0 16px 16px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PriceSummaryRow = styled.div`
  height: 33px;
  box-sizing: border-box;
  border-top: 1px solid ${(props) => props.theme.colors.GREY_100};
  padding: 16px 2px 0 2px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const PriceSummaryLabel = styled.div`
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  color: #48515b;
`;

const PriceSummaryTotal = styled.div`
  display: flex;
  align-items: baseline;
  gap: 2px;
`;

const PriceSummaryTotalAmount = styled.span`
  font-size: 24px;
  font-weight: 500;
  line-height: 100%;
  color: ${(props) => props.theme.colors.GREY_900};
`;

const PriceSummaryTotalUnit = styled.span`
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: ${(props) => props.theme.colors.GREY_900};
`;

const CheckoutButton = styled.button`
  width: 100%;
  height: 32px;
  border-radius: 8px;
  border: none;
  background: ${(props) => props.theme.colors.GREY_900};
  color: ${(props) => props.theme.colors.WHITE};
  font-size: 12px;
  line-height: 16px;
  font-weight: 500;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const FeatureTextUnderline = styled.a`
  text-decoration: underline;
  color: ${(props) => props.theme.colors.GREY_500};
`;

const ORG_ROLE_OWNER = "Owner";
const ORG_ROLE_ADMIN = "Admin";

function getOrgAdminOrOwnerEmail(users: OrganizationUserDto[]): string | null {
  const withRole = (roleName: string) =>
    users.find((u) => u.assignments?.some((a) => a.role?.name === roleName));
  const owner = withRole(ORG_ROLE_OWNER);
  if (owner?.email) return owner.email;
  const admin = withRole(ORG_ROLE_ADMIN);
  return admin?.email ?? null;
}

const CONTACT_ADMIN_SUBJECT = "Request to upgrade our Superblocks AI plan";
const CONTACT_ADMIN_BODY =
  "Hi,\n\nI need help upgrading our organization's AI plan to continue using Clark AI. Could you please look into this?\n\nThanks.";

/**
 * Standard informational quota/limit modal.
 *
 * Shares the chrome used by the rest of the app's small modals (see
 * `ContactAdminBillingModal`): a 48px header with a left-aligned title + close
 * button, a left-aligned body, and a right-aligned footer with a ghost "Close"
 * and an optional primary action. Used for the per-user and org/enterprise
 * limit states, which previously rendered a non-standard centered layout with a
 * bare ⚠️ emoji and ad-hoc inline styles.
 *
 * Styled with styled-components rather than Tailwind utility classes because
 * Tailwind's content scanner does not reliably compile classes for files in
 * `packages/ui/src/core/ai/` when the modal is rendered from the Clark chat
 * surface — observed mid-PR: `bg-black` + `!text-white` produced an invisible
 * white-on-white primary button and `justify-end` did not apply, leaving Close
 * mis-aligned. Inline styled-components match the visual spec of
 * `ContactAdminBillingModal` and are scope-independent.
 */
const StandardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 48px;
  border-bottom: 1px solid #e5e7eb;
  padding-left: 24px;
  padding-right: 10px;
`;
const StandardTitle = styled.h2`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  color: #030712;
`;
const StandardCloseX = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 32px;
  width: 32px;
  border: none;
  background: transparent;
  border-radius: 6px;
  color: #6b7280;
  cursor: pointer;
  font-size: 24px;
  font-weight: 300;
  line-height: 1;
  padding: 0;
  transition:
    background 0.12s ease,
    color 0.12s ease;
  &:hover {
    background: #f9fafb;
    color: #374151;
  }
`;
const StandardBody = styled.div`
  padding: 20px 24px;
`;
const StandardBodyText = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 24px;
  color: #44505f;
  a {
    color: #0099ff;
    font-weight: 500;
    text-decoration: none;
  }
  a:hover {
    text-decoration: underline;
  }
`;
const StandardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  border-top: 1px solid #e5e7eb;
  padding: 16px 24px;
`;
const StandardCloseBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  padding: 0 16px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  color: #111827;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.12s ease;
  &:hover {
    background: #f9fafb;
  }
`;
const StandardPrimaryBtn = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  padding: 0 20px;
  background: #000000;
  color: #ffffff !important;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  text-decoration: none;
  transition: background 0.12s ease;
  &:hover {
    background: rgba(0, 0, 0, 0.9);
    color: #ffffff !important;
    text-decoration: none;
  }
`;

function QuotaInfoModal({
  open,
  onClose,
  title,
  body,
  primaryAction,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  body: ReactNode;
  /**
   * Optional primary CTA rendered as a link. Opens in a new tab so the user
   * keeps Clark / the in-app surface in their current tab.
   */
  primaryAction?: { label: string; href: string };
}) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-label={title}
      closable={false}
      className="w-120 overflow-hidden"
      bodyClassName="p-0"
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          background: "#FFFFFF",
        }}
      >
        <StandardHeader>
          <StandardTitle>{title}</StandardTitle>
          <StandardCloseX type="button" onClick={onClose} aria-label="Close">
            ×
          </StandardCloseX>
        </StandardHeader>

        <StandardBody>
          <StandardBodyText>{body}</StandardBodyText>
        </StandardBody>

        <StandardFooter>
          <StandardCloseBtn type="button" onClick={onClose}>
            Close
          </StandardCloseBtn>
          {primaryAction ? (
            <StandardPrimaryBtn
              href={primaryAction.href}
              target="_blank"
              rel="noopener noreferrer"
              onClick={onClose}
            >
              {primaryAction.label}
            </StandardPrimaryBtn>
          ) : null}
        </StandardFooter>
      </div>
    </Modal>
  );
}

export function AiQuotaPaywallModalBase({
  open,
  reason,
  onClose,
  analyticsSurface,
  analyticsMetadata,
  onSuccess,
  returnTo: returnToProp,
  titleOverride,
  skipPlanCheck,
}: AiQuotaPaywallModalBaseProps) {
  const organization = useSelector(selectOnlyOrganization);
  const organizationId = useSelector(selectOnlyOrganizationId);
  const [canManageBilling] = useAuthorizationCheck([MANAGE_BILLING]);
  const defaultPricing = useDefaultPricing();
  const cloudPrem = isCloudPrem();
  const billingPlan = organization?.billing?.plan;
  const stripeSubStatus = organization?.billing?.stripeSubStatus;
  const isTrialing =
    billingPlan === BillingPlan.TEAMS && stripeSubStatus === "trialing";
  const isPastDue =
    billingPlan === BillingPlan.TEAMS && stripeSubStatus === "past_due";
  // Past-due routes to ManagePlanModal regardless of `reason`, because the
  // dedicated "Update payment method" UI lives there. Without this, a
  // payment_past_due reason on a TEAMS+past_due org would still hit the
  // generic paywall flow.
  const shouldShowPaywallModal =
    !isPastDue && (isTrialLikePlan(billingPlan) || isTrialing);
  const shouldUseManagePlanModal =
    billingPlan === BillingPlan.TEAMS && !isTrialing;
  const isEnterprise = isEnterpriseLikePlan(billingPlan);
  // POC is enterprise-like for paywall routing but has self-serve refills,
  // so the body copy points to /spending (auto-refill / buy GAUs) instead
  // of "contact your account team to adjust your contract."
  const isPocPlan = billingPlan === BillingPlan.POC;
  // Usage/credit-LIMIT reasons whose modal has a meaningful, non-Stripe action
  // on cloud-prem (ask an admin / Usage page / contact your account team). These
  // must surface on cloud-prem — unlike the Stripe-backed paywalls (trial
  // conversion, past-due, upgrade) which have no actionable path there. Without
  // this, a cloud-prem builder who hits a per-user or org limit is blocked with
  // no cue (ENG-4379).
  const isCloudPremActionableReason =
    reason === "user_credit_limit_exceeded" ||
    reason === "credit_limit_exceeded" ||
    reason === "token_limit_exceeded" ||
    reason === "dollar_commit_exhausted" ||
    // Provisioning failures render an info modal (retry / contact support),
    // which is just as actionable on cloud-prem as the limit reasons above.
    reason === "provisioning_error";
  const shouldCloseUnsupportedPlan =
    !cloudPrem &&
    !skipPlanCheck &&
    open &&
    billingPlan != null &&
    !shouldShowPaywallModal &&
    !shouldUseManagePlanModal &&
    !isEnterprise;

  const [view, setView] = useState<"plan-selection" | "seat-config">(
    "plan-selection",
  );
  const orgInterval = organization?.billing?.billingInterval;
  // Seatless (credits-pricing) orgs buy a GAU pack instead of licenses: the
  // second screen swaps the seat stepper for a pack picker, and checkout /
  // end-trial calls carry `aiCreditPack` instead of `builderSeats`.
  const isSeatless = isCreditsPricing(organization?.billing?.pricingModel);
  const [seats, setSeats] = useState(1);
  const [packCredits, setPackCredits] = useState(
    () => defaultCreditPack("monthly").credits,
  );
  const [isAnnual, setIsAnnual] = useState(orgInterval !== "monthly");
  const packInterval = isAnnual ? "annual" : "monthly";
  const packOptions = creditPackOptionsForInterval(packInterval);
  const selectedPack =
    packOptions.find((option) => option.credits === packCredits) ??
    packOptions[0];
  const [isRedirectingToCheckout, setIsRedirectingToCheckout] = useState(false);
  const [isEndingTrial, setIsEndingTrial] = useState(false);

  // Trialing seatless orgs can change pack and interval at conversion (end-trial
  // applies both), so the picker renders for them too. Their checkout-time pack
  // is only the picker's DEFAULT selection — fetched from plan-summary because
  // the pack size lives on the ai_credits entitlement, not the org DTO.
  //
  // Two race guards around that async seed:
  //  - `userPickedPackRef` — a selection made while the fetch is in flight must
  //    never be clobbered by the late-resolving default.
  //  - `committedPackSettled` / `committedPackLoaded` — the Upgrade CTA stays
  //    disabled until the seed attempt settles, and end-trial only SENDS
  //    `aiCreditPack` once the default really loaded (or the user explicitly
  //    picked). Otherwise a fast click or a failed fetch would convert the org
  //    onto the picker's placeholder minimum pack instead of carrying the
  //    checkout-time pack forward (the server's no-field behavior).
  const userPickedPackRef = useRef(false);
  // Same intent-tracking for the interval toggle: it DEFAULTS to annual when
  // the org DTO interval is null, so end-trial must not send that guess.
  const userTouchedIntervalRef = useRef(false);
  const [committedPackSettled, setCommittedPackSettled] = useState(false);
  const [committedPackLoaded, setCommittedPackLoaded] = useState(false);
  useEffect(() => {
    if (!open || !isSeatless || !isTrialing) return;
    let cancelled = false;
    (async () => {
      try {
        const summary = await callServer<{
          committedPackCredits?: number | null;
        }>(
          { method: HttpMethod.Get, url: "v1/billing/plan-summary" },
          { notifyOnError: false },
        );
        // ONLY the explicit pack field is trustworthy here: this effect runs
        // for TRIALING orgs, whose currentPlanCredits reports the LD trial
        // allowance (e.g. 20) — deriving a pack from it would seed the picker
        // with an allowance-sized pack AND mark it "loaded", making end-trial
        // send that wrong pack instead of carrying the subscription pack
        // forward. No field = no seed = the send-guard omits aiCreditPack.
        const packCredits = summary?.committedPackCredits ?? null;
        if (!cancelled && packCredits != null) {
          if (!userPickedPackRef.current) {
            setPackCredits(committedPackCredits(packCredits));
          }
          setCommittedPackLoaded(true);
        }
      } catch (err) {
        logger.warn("Failed to load committed pack for trial upgrade", {
          message: err instanceof Error ? err.message : String(err),
        });
      } finally {
        if (!cancelled) {
          setCommittedPackSettled(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, isSeatless, isTrialing]);
  const onPickPack = useCallback((credits: number) => {
    userPickedPackRef.current = true;
    setPackCredits(credits);
  }, []);
  // Keep the toggle's DISPLAYED state honest if orgInterval hydrates while the
  // modal is already open: the reset effect below only runs on close, so a
  // late-arriving interval would otherwise leave the toggle showing its null
  // default (annual) even after the real interval is known. Never override a
  // deliberate user flip.
  useEffect(() => {
    if (!open || userTouchedIntervalRef.current) return;
    if (orgInterval === "monthly" || orgInterval === "annual") {
      setIsAnnual(orgInterval === "annual");
    }
  }, [open, orgInterval]);
  const displayPackInterval = packInterval;
  const displayPack = selectedPack;

  useEffect(() => {
    if (!open) {
      setView("plan-selection");
      setIsRedirectingToCheckout(false);
      setSeats(1);
      setPackCredits(defaultCreditPack("monthly").credits);
      setIsAnnual(orgInterval !== "monthly");
      userPickedPackRef.current = false;
      userTouchedIntervalRef.current = false;
      setCommittedPackSettled(false);
      setCommittedPackLoaded(false);
    }
  }, [open, orgInterval]);

  useEffect(() => {
    if (!open) {
      return;
    }
    // Suppress telemetry only when the modal itself is suppressed on cloud-prem
    // (Stripe-backed reasons). Actionable limit modals DO render on cloud-prem,
    // so log them.
    if (cloudPrem && !isCloudPremActionableReason) {
      return;
    }

    logger.info("[AiQuota] Paywall modal shown", {
      reason: reason ?? "unknown",
      surface: analyticsSurface,
      ...analyticsMetadata,
    });
  }, [
    analyticsMetadata,
    analyticsSurface,
    cloudPrem,
    isCloudPremActionableReason,
    open,
    reason,
  ]);

  useEffect(() => {
    if (!shouldCloseUnsupportedPlan) {
      return;
    }

    onClose();
  }, [onClose, shouldCloseUnsupportedPlan]);

  const onUpgrade = useCallback(async () => {
    if (isRedirectingToCheckout) {
      return;
    }
    setIsRedirectingToCheckout(true);
    try {
      const returnTo = returnToProp ?? "/usage";
      const response = await callServer<{ checkoutUrl: string }>(
        {
          method: HttpMethod.Post,
          url: "v1/billing/create-checkout-session",
          body: {
            returnTo,
            billingInterval: isAnnual ? "annual" : "monthly",
            ...(isSeatless
              ? { aiCreditPack: selectedPack.credits }
              : { builderSeats: seats }),
            embedded: false,
          },
        },
        { notifyOnError: false },
      );
      window.location.assign(response.checkoutUrl);
    } catch (err) {
      logger.warn("Create checkout session failed, opening fallback URL", {
        message: err instanceof Error ? err.message : String(err),
      });
      window.open(AI_QUOTA_PAYWALL_STRIPE_CHECKOUT_URL, "_blank");
      setIsRedirectingToCheckout(false);
    }
  }, [
    seats,
    isAnnual,
    isRedirectingToCheckout,
    returnToProp,
    isSeatless,
    selectedPack.credits,
  ]);

  const onEndTrial = useCallback(
    async (builderSeats?: number) => {
      if (isEndingTrial) return;
      setIsEndingTrial(true);
      try {
        await callServer<{ success: boolean }>(
          {
            method: HttpMethod.Post,
            url: "v1/billing/end-trial",
            // Guarded builder: pack and interval are only sent when they
            // reflect user intent or genuinely-loaded data; omitted fields
            // make the server carry the subscription's current values forward.
            // See buildEndTrialRequestBody for the full rationale.
            body: buildEndTrialRequestBody({
              isSeatless,
              seats: builderSeats ?? seats,
              selectedPackCredits: selectedPack.credits,
              isAnnual,
              userPickedPack: userPickedPackRef.current,
              committedPackLoaded,
              userTouchedInterval: userTouchedIntervalRef.current,
              orgIntervalKnown:
                orgInterval === "monthly" || orgInterval === "annual",
            }),
          },
          { notifyOnError: false },
        );
        window.location.reload();
      } catch (err) {
        logger.warn("End trial failed", {
          message: err instanceof Error ? err.message : String(err),
        });
        sendErrorUINotification({
          message: "Failed to convert to Teams. Please try again.",
        });
        setIsEndingTrial(false);
      }
    },
    [
      isEndingTrial,
      seats,
      isAnnual,
      isSeatless,
      selectedPack.credits,
      committedPackLoaded,
      orgInterval,
    ],
  );

  const onContactAdmin = useCallback(async () => {
    if (!organizationId) return;
    try {
      const users = await callServer<OrganizationUserDto[]>(
        {
          method: HttpMethod.Get,
          url: `v1/organizations/${organizationId}/users`,
        },
        { notifyOnError: false, shouldThrowHttpError: true },
      );
      const email = getOrgAdminOrOwnerEmail(users ?? []);
      if (email) {
        const mailto = `mailto:${encodeURIComponent(email)}?subject=${encodeURIComponent(CONTACT_ADMIN_SUBJECT)}&body=${encodeURIComponent(CONTACT_ADMIN_BODY)}`;
        window.open(mailto, "_blank");
      } else {
        sendErrorUINotification({
          message:
            "No organization admin or owner could be found. Ask your admin to upgrade your plan.",
        });
      }
    } catch (err: unknown) {
      const code =
        err && typeof err === "object" && "code" in err
          ? (err as { code: number }).code
          : null;
      if (code === 403) {
        sendErrorUINotification({
          message:
            "You don't have permission to view organization admins. Ask your admin to upgrade your plan.",
        });
      } else {
        sendErrorUINotification({
          message:
            "Could not load organization admins. Ask your admin to upgrade your plan.",
        });
      }
    }
  }, [organizationId]);

  // Cloud-prem deployments pay by contract; Stripe-backed billing surfaces
  // have no price IDs and their paywall has no meaningful action, so those stay
  // suppressed. Usage/credit-LIMIT modals DO have a meaningful action on
  // cloud-prem (ask an admin / Usage page) and must surface — otherwise a
  // cloud-prem builder who hits a per-user or org limit is blocked with no cue.
  // Those fall through to the QuotaInfoModal branches below (cloud-prem orgs are
  // enterprise-like, so they pass the plan gate). (ENG-4379)
  if (cloudPrem && !(isCloudPremActionableReason && open)) {
    return null;
  }

  // TRIAL → paywall modal, TEAMS → ManagePlanModal, else → no modal.
  // When skipPlanCheck is set the caller (e.g. deploy quota guard) already
  // validated the quota on the server, so we render the paywall regardless
  // of the client-side billing plan (which may lag behind).
  if (
    !skipPlanCheck &&
    !shouldShowPaywallModal &&
    !shouldUseManagePlanModal &&
    !isEnterprise
  ) {
    return null;
  }

  // Provisioning failure, not exhaustion: the org's entitlement row hasn't been
  // written yet (webhook delayed/failed). Never route this into a purchase flow —
  // the org already paid, and buying more won't create the missing row.
  if (reason === "provisioning_error" && open) {
    const cfg = AI_QUOTA_PAYWALL_REASON_CONFIG.provisioning_error;
    return (
      <QuotaInfoModal
        open={open}
        onClose={onClose}
        title={cfg.title}
        body={cfg.subtitle}
      />
    );
  }

  if (reason === "user_credit_limit_exceeded" && open) {
    return (
      <QuotaInfoModal
        open={open}
        onClose={onClose}
        title="You've reached your assigned usage limit"
        body={
          canManageBilling ? (
            <>
              You can adjust per-user usage limits on the{" "}
              <a
                href="/spending"
                target="_blank"
                rel="noopener noreferrer"
                onClick={onClose}
              >
                Spend Management page
              </a>
              .
            </>
          ) : (
            "Contact an admin to request an increase."
          )
        }
        primaryAction={
          canManageBilling
            ? { label: "Go to Spend Management", href: "/spending" }
            : undefined
        }
      />
    );
  }

  if (shouldUseManagePlanModal) {
    return (
      <ManagePlanModal
        open={open}
        onClose={onClose}
        onSuccess={onSuccess}
        canManageBilling={canManageBilling}
        reason={reason}
      />
    );
  }

  if (isEnterprise && open) {
    const reasonConfig = reason ? AI_QUOTA_PAYWALL_REASON_CONFIG[reason] : null;
    const orgTitle =
      reasonConfig?.title ?? "Your organization has reached its AI usage limit";

    let body: ReactNode;
    let primaryAction: { label: string; href: string } | undefined;
    if (canManageBilling) {
      if (isPocPlan) {
        body = (
          <>
            Enable auto-refill on the{" "}
            <a
              href="/spending"
              target="_blank"
              rel="noopener noreferrer"
              onClick={onClose}
            >
              Spending page
            </a>{" "}
            to keep building.
          </>
        );
        primaryAction = { label: "Go to Spending page", href: "/spending" };
      } else {
        body = (
          <>
            Contact your account team to adjust your contract, or{" "}
            <a
              href="/usage"
              target="_blank"
              rel="noopener noreferrer"
              onClick={onClose}
            >
              adjust overage settings on the Usage page
            </a>
            .
          </>
        );
        primaryAction = { label: "Go to Usage page", href: "/usage" };
      }
    } else {
      body =
        typeof reasonConfig?.subtitle === "string" ? (
          <GauInText>{reasonConfig.subtitle}</GauInText>
        ) : (
          (reasonConfig?.subtitle ?? "Contact your admin for assistance.")
        );
    }

    return (
      <QuotaInfoModal
        open={open}
        onClose={onClose}
        title={orgTitle}
        body={body}
        primaryAction={primaryAction}
      />
    );
  }

  const expiryDate = organization?.billing?.expiryDate
    ? new Date(organization.billing.expiryDate)
    : null;
  const isTrialExpired =
    billingPlan === BillingPlan.TRIAL &&
    expiryDate != null &&
    expiryDate.getTime() < Date.now();
  const config = AI_QUOTA_PAYWALL_REASON_CONFIG.trial_expired;
  const title =
    titleOverride ??
    (isTrialing
      ? "Upgrade your plan now"
      : isTrialExpired
        ? config.title
        : "Upgrade your plan");

  const price = isAnnual
    ? defaultPricing.annualPrice / 12
    : defaultPricing.monthlyPrice;
  const totalPrice = seats * price;
  const totalPriceFormatted = totalPrice.toLocaleString("en-US");

  const annualToggle = (
    <AnnualToggleWrapper>
      <span>
        Annual <Save20Text>(Save 20%)</Save20Text>
      </span>
      <Switch
        size="sm"
        className="data-[state=checked]:bg-sb-grey-900"
        checked={isAnnual}
        onCheckedChange={(checked) => {
          userTouchedIntervalRef.current = true;
          setIsAnnual(checked);
        }}
      />
    </AnnualToggleWrapper>
  );

  // Seatless orgs have no per-builder allowance — the pack pool is shared
  // org-wide, so the seat-framed line becomes shared-pool copy followed by
  // the invite line (inviting builders is how the pool gains people now).
  const teamsAiFeatures = isSeatless
    ? seatlessAiFeatureList(
        AI_QUOTA_PAYWALL_TEAMS_FEATURES,
        displayPack.credits,
      )
    : AI_QUOTA_PAYWALL_TEAMS_FEATURES;
  const renderTeamsFeatureText = (feature: string) => (
    <span>
      <GauPacksInText
        renderLink={() => (
          <FeatureTextUnderline
            href={GAU_PACKS_DOCS_URL}
            rel="noreferrer"
            target="_blank"
          >
            GAU Packs
          </FeatureTextUnderline>
        )}
      >
        {feature}
      </GauPacksInText>
    </span>
  );

  const teamsFeaturesSection = (
    <FeaturesContainer>
      <IncludesDivider>Includes:</IncludesDivider>
      <FeatureSection>
        <SectionTitle>
          AI & <GauTerm plural />
        </SectionTitle>
        <FeatureList>
          {teamsAiFeatures.map((feature) => (
            <FeatureItem key={feature}>
              <CheckOutlined
                style={{ color: "#0099FF", fontSize: 12, marginTop: 2 }}
              />
              {renderTeamsFeatureText(feature)}
            </FeatureItem>
          ))}
        </FeatureList>
      </FeatureSection>
      <FeatureSection>
        <SectionTitle>Platform</SectionTitle>
        <FeatureList>
          {AI_QUOTA_PAYWALL_TEAMS_PLATFORM_FEATURES.map((feature) => (
            <FeatureItem key={feature}>
              <CheckOutlined
                style={{ color: "#0099FF", fontSize: 12, marginTop: 2 }}
              />
              <span>{feature}</span>
            </FeatureItem>
          ))}
        </FeatureList>
      </FeatureSection>
    </FeaturesContainer>
  );

  const secondScreenFeaturesSections = (
    <>
      <FeatureSection>
        <SectionTitle>
          AI & <GauTerm plural />
        </SectionTitle>
        <FeatureList>
          {teamsAiFeatures.map((feature) => (
            <FeatureItem key={feature}>
              <CheckOutlined
                style={{ color: "#0099FF", fontSize: 12, marginTop: 2 }}
              />
              {renderTeamsFeatureText(feature)}
            </FeatureItem>
          ))}
        </FeatureList>
      </FeatureSection>
      <FeatureSection>
        <SectionTitle>Platform</SectionTitle>
        <FeatureList>
          {AI_QUOTA_PAYWALL_TEAMS_PLATFORM_FEATURES.map((feature) => (
            <FeatureItem key={feature}>
              <CheckOutlined
                style={{ color: "#0099FF", fontSize: 12, marginTop: 2 }}
              />
              <span>{feature}</span>
            </FeatureItem>
          ))}
        </FeatureList>
      </FeatureSection>
    </>
  );

  const modalTitle = (
    <span style={{ display: "flex", alignItems: "center" }}>
      <ModalTitleText>{title}</ModalTitleText>
      <ModalTitleDivider />
      <ModalTitleSubtext>
        Please purchase a self-serve plan or contact sales to discuss enterprise
        pricing
      </ModalTitleSubtext>
    </span>
  );

  const seatConfigTitle = (
    <SeatConfigTitleRow>
      <BackButton
        type="button"
        onClick={() => setView("plan-selection")}
        aria-label="Back to plan selection"
      >
        <ArrowLeftOutlined />
      </BackButton>
      <SeatConfigTitleCenter>
        <ModalTitleText>{title}</ModalTitleText>
      </SeatConfigTitleCenter>
    </SeatConfigTitleRow>
  );

  const seatConfigContent = (
    <PlanCard data-highlighted="true">
      <SecondScreenBody>
        <SeatConfigBodyTop>
          <PlanTopRow>
            <PlanName>Teams</PlanName>
            {annualToggle}
          </PlanTopRow>
          <PlanCardContent>
            <PriceRow>
              <PlanPrice>
                ${isSeatless ? displayPack.priceUsd.toLocaleString() : price}
              </PlanPrice>
              <PlanPriceUnit>
                {isSeatless
                  ? displayPackInterval === "annual"
                    ? "/ year"
                    : "/ month"
                  : "/ AI Builder / month"}
              </PlanPriceUnit>
            </PriceRow>
            <PlanDescription>
              Everything you need to build and deploy internal tools.
            </PlanDescription>
            {isSeatless ? (
              <SeatConfigCard>
                <SeatConfigTop data-test="pack-config-section">
                  Choose your Governed Agent Units (<GauTerm />) pack
                </SeatConfigTop>
                <SeatConfigMiddle>
                  <CreditPackSelect
                    dataTest="paywall-pack-select"
                    options={packOptions}
                    selectedCredits={selectedPack.credits}
                    onSelect={onPickPack}
                    billingInterval={packInterval}
                    // While a trialing org's committed-pack default is still loading, lock
                    // the picker: otherwise a pick of the visible placeholder would set
                    // userPickedPackRef and both block the seed AND send that placeholder.
                    disabled={isTrialing && !committedPackSettled}
                  />
                </SeatConfigMiddle>
                <SeatConfigBottom>
                  <PriceSummaryRow>
                    <PriceSummaryLabel>
                      {displayPack.credits.toLocaleString()} <GauTerm plural />{" "}
                      pack
                    </PriceSummaryLabel>
                    <PriceSummaryTotal>
                      <PriceSummaryTotalAmount>
                        ${displayPack.priceUsd.toLocaleString()}
                      </PriceSummaryTotalAmount>
                      <PriceSummaryTotalUnit>
                        {displayPackInterval === "annual" ? "/yr" : "/mo"}
                      </PriceSummaryTotalUnit>
                    </PriceSummaryTotal>
                  </PriceSummaryRow>
                  <a
                    href={GAU_PACKS_DOCS_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="block pb-3 text-xs leading-4 text-gray-500 underline hover:text-gray-700"
                  >
                    Includes 1 deployed app per team
                  </a>
                  <CheckoutButton
                    data-test="paywall-checkout-btn"
                    onClick={isTrialing ? () => onEndTrial() : onUpgrade}
                    disabled={
                      isTrialing
                        ? // Gate on the committed-pack seed settling so a fast
                          // click can't convert onto the placeholder default.
                          isEndingTrial || !committedPackSettled
                        : isRedirectingToCheckout
                    }
                  >
                    {isTrialing
                      ? isEndingTrial
                        ? "Upgrading..."
                        : "Upgrade"
                      : isRedirectingToCheckout
                        ? "Redirecting..."
                        : "Checkout now"}
                  </CheckoutButton>
                </SeatConfigBottom>
              </SeatConfigCard>
            ) : (
              <SeatConfigCard>
                <SeatConfigTop data-test="seat-config-section">
                  Choose number of AI Builder licenses
                </SeatConfigTop>
                <SeatConfigMiddle>
                  <StepperButton
                    onClick={() =>
                      setSeats((s) =>
                        Math.max(AI_QUOTA_PAYWALL_MIN_SEATS, s - 1),
                      )
                    }
                    disabled={seats <= AI_QUOTA_PAYWALL_MIN_SEATS}
                    aria-label="Decrease licenses"
                  >
                    <MinusOutlined style={{ fontSize: 16 }} />
                  </StepperButton>
                  <SeatCount>{seats}</SeatCount>
                  <StepperButton
                    onClick={() =>
                      setSeats((s) =>
                        Math.min(AI_QUOTA_PAYWALL_MAX_SEATS, s + 1),
                      )
                    }
                    disabled={seats >= AI_QUOTA_PAYWALL_MAX_SEATS}
                    aria-label="Increase licenses"
                  >
                    <PlusOutlined style={{ fontSize: 16 }} />
                  </StepperButton>
                </SeatConfigMiddle>
                <SeatConfigBottom>
                  <PriceSummaryRow>
                    <PriceSummaryLabel>
                      {seats} {seats === 1 ? "license" : "licenses"} × ${price}
                      /mo
                    </PriceSummaryLabel>
                    <PriceSummaryTotal>
                      <PriceSummaryTotalAmount>
                        ${totalPriceFormatted}
                      </PriceSummaryTotalAmount>
                      <PriceSummaryTotalUnit>/mo</PriceSummaryTotalUnit>
                    </PriceSummaryTotal>
                  </PriceSummaryRow>
                  <a
                    href={GAU_PACKS_DOCS_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="block pb-3 text-xs leading-4 text-gray-500 underline hover:text-gray-700"
                  >
                    Includes {seats * AI_QUOTA_PAYWALL_CREDITS_PER_SEAT}{" "}
                    <GauTerm
                      plural={seats * AI_QUOTA_PAYWALL_CREDITS_PER_SEAT !== 1}
                    />{" "}
                    + 1 deployed app per team
                  </a>
                  <CheckoutButton
                    data-test="paywall-checkout-btn"
                    onClick={isTrialing ? () => onEndTrial(seats) : onUpgrade}
                    disabled={
                      isTrialing ? isEndingTrial : isRedirectingToCheckout
                    }
                  >
                    {isTrialing
                      ? isEndingTrial
                        ? "Upgrading..."
                        : "Upgrade"
                      : isRedirectingToCheckout
                        ? "Redirecting..."
                        : "Checkout now"}
                  </CheckoutButton>
                </SeatConfigBottom>
              </SeatConfigCard>
            )}
          </PlanCardContent>
        </SeatConfigBodyTop>
        {secondScreenFeaturesSections}
      </SecondScreenBody>
    </PlanCard>
  );

  if (view === "seat-config") {
    return (
      <Modal
        open={open}
        title={seatConfigTitle}
        onClose={onClose}
        className="max-h-11/12 w-123"
        titleClassName="pl-3"
        bodyClassName="min-h-0 overflow-y-auto"
      >
        <div data-test="paywall-seat-config">{seatConfigContent}</div>
      </Modal>
    );
  }

  const planSelectionContent = (
    <div className="grid grid-cols-2 gap-x-6">
      <div>
        <PlanCard data-highlighted="true">
          <PlanCardTop>
            <PlanTopRow>
              <PlanName>Teams</PlanName>
              {annualToggle}
            </PlanTopRow>
            <PlanCardContent>
              <PriceRow>
                {isSeatless ? (
                  <>
                    <PlanPrice>
                      From $
                      {defaultCreditPack(
                        packInterval,
                      ).priceUsd.toLocaleString()}
                    </PlanPrice>
                    <PlanPriceUnit>
                      {isAnnual ? "/ year" : "/ month"}
                    </PlanPriceUnit>
                  </>
                ) : (
                  <>
                    <PlanPrice>${price}</PlanPrice>
                    <PlanPriceUnit>/ AI Builder / month</PlanPriceUnit>
                  </>
                )}
              </PriceRow>
              <PlanDescription>
                Everything you need to build and deploy internal tools.
              </PlanDescription>
              {canManageBilling ? (
                <CTAButton
                  data-primary="true"
                  type="default"
                  onClick={() => setView("seat-config")}
                >
                  {isTrialing ? "Upgrade now" : "Upgrade"}
                </CTAButton>
              ) : (
                <CTAButton
                  type="default"
                  onClick={onContactAdmin}
                  disabled={!organizationId}
                >
                  Contact your admin to upgrade
                </CTAButton>
              )}
            </PlanCardContent>
          </PlanCardTop>
          {teamsFeaturesSection}
        </PlanCard>
      </div>

      <div>
        <PlanCard>
          <PlanCardTop>
            <PlanTopRow>
              <PlanName>Enterprise</PlanName>
            </PlanTopRow>
            <PlanCardContent>
              <PriceRow>
                <PlanPrice>Custom</PlanPrice>
              </PriceRow>
              <PlanDescription>
                Advanced security, deployment, and support.
              </PlanDescription>
              <CTAButton
                type="default"
                onClick={() =>
                  window.open(AI_QUOTA_PAYWALL_CONTACT_SALES_URL, "_blank")
                }
              >
                Contact us
              </CTAButton>
            </PlanCardContent>
          </PlanCardTop>
          <FeaturesContainer>
            <IncludesDivider>Everything in Teams, plus:</IncludesDivider>
            <FeatureSection>
              <SectionTitle>AI Platform</SectionTitle>
              <FeatureList>
                {AI_QUOTA_PAYWALL_ENTERPRISE_FEATURES.map((feature) => (
                  <FeatureItem key={feature}>
                    <CheckOutlined
                      style={{
                        color: "#0099FF",
                        fontSize: 12,
                        marginTop: 2,
                      }}
                    />
                    {feature.startsWith("Source Control") ? (
                      <>
                        <span>Source Control</span>
                        <WithTooltip content={feature}>
                          <InfoIcon size={12} className="ml-1 text-[#8c8c8c]" />
                        </WithTooltip>
                      </>
                    ) : (
                      <span>{feature}</span>
                    )}
                  </FeatureItem>
                ))}
              </FeatureList>
            </FeatureSection>
            <FeatureSection>
              <SectionTitle>Security & Compliance</SectionTitle>
              <FeatureList>
                {AI_QUOTA_PAYWALL_ENTERPRISE_PLATFORM_FEATURES.map(
                  (feature) => (
                    <FeatureItem key={feature}>
                      <CheckOutlined
                        style={{
                          color: "#0099FF",
                          fontSize: 12,
                          marginTop: 2,
                        }}
                      />
                      <span>{feature}</span>
                    </FeatureItem>
                  ),
                )}
              </FeatureList>
            </FeatureSection>
            <FeatureSection>
              <SectionTitle>Support</SectionTitle>
              <FeatureList>
                {AI_QUOTA_PAYWALL_ENTERPRISE_SUPPORT_FEATURES.map((feature) => (
                  <FeatureItem key={feature}>
                    <CheckOutlined
                      style={{
                        color: "#0099FF",
                        fontSize: 12,
                        marginTop: 2,
                      }}
                    />
                    <span>{feature}</span>
                  </FeatureItem>
                ))}
              </FeatureList>
            </FeatureSection>
          </FeaturesContainer>
        </PlanCard>
      </div>
    </div>
  );

  return (
    <Modal
      open={open}
      title={modalTitle}
      onClose={onClose}
      className="max-h-11/12 w-194"
      titleClassName="pl-6"
      bodyClassName="min-h-0 overflow-y-auto"
    >
      <div data-test="paywall-plan-selection">{planSelectionContent}</div>
    </Modal>
  );
}
